import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  ChevronDown, 
  ArrowUpDown,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  User,
  Briefcase,
  Star,
  Calendar
} from 'lucide-react';
import { useToast } from '../components/shared/ToastProvider';
import { useButtonClick } from '../hooks/useInteractiveElement';
import AddSubmissionModal from '../components/AddSubmissionModal';
import SubmissionViewModal from '../components/SubmissionViewModal';
import ClientSubmissionEditModal from '../components/ClientSubmissionEditModal';
import { formatSubmissionId } from '../utils/idFormatter';

const Submissions = () => {
  const { showToast } = useToast();
  const { handleClick, isLoading } = useButtonClick();
  
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [showClientEditModal, setShowClientEditModal] = useState(false);
  
  // Get current user role from URL path or localStorage
  const getCurrentRole = () => {
    const pathname = window.location.pathname;
    if (pathname.includes('/recruiter-riley')) return 'recruiter';
    if (pathname.includes('/lead-lena')) return 'lead_recruiter';
    if (pathname.includes('/sales-sam')) return 'sales';
    if (pathname.includes('/lead-sales')) return 'lead_sales_manager';
    return localStorage.getItem('selectedPersona') || 'recruiter';
  };
  const currentUserRole = getCurrentRole();
  
  // Table controls
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [sortField, setSortField] = useState("submission_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openActionMenus, setOpenActionMenus] = useState({});

  useEffect(() => {
    fetchSubmissions();
  }, []);

  // Close action menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setOpenActionMenus({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions`);
      if (response.ok) {
        const data = await response.json();
        // Backend returns {submissions: [...], pagination: {...}, filters_applied: {...}}
        setSubmissions(data.submissions && Array.isArray(data.submissions) ? data.submissions : []);
      } else {
        showToast('Failed to fetch submissions', 'error');
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      showToast('Error fetching submissions', 'error');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'sourced': 'bg-blue-100 text-blue-800',
      'screened': 'bg-purple-100 text-purple-800',
      'submitted': 'bg-yellow-100 text-yellow-800',
      'client_reviewing': 'bg-orange-100 text-orange-800',
      'client_accepted': 'bg-green-100 text-green-800',
      'client_rejected': 'bg-red-100 text-red-800',
      'interview_scheduled': 'bg-indigo-100 text-indigo-800',
      'interview_scheduled': 'bg-indigo-100 text-indigo-800',
      'interview_scheduled': 'bg-indigo-100 text-indigo-800',
      'interview_passed': 'bg-green-100 text-green-800',
      'interview_failed': 'bg-red-100 text-red-800',
      'offer_made': 'bg-emerald-100 text-emerald-800',
      'offer_accepted': 'bg-green-100 text-green-800',
      'offer_rejected': 'bg-red-100 text-red-800',
      'placed': 'bg-green-100 text-green-800',
      'started': 'bg-blue-100 text-blue-800',
      'rejected': 'bg-red-100 text-red-800',
      'withdrawn': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `$${Number(amount).toLocaleString()}`;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Toggle actions menu for specific submission
  const toggleActionMenu = (submissionId) => {
    setOpenActionMenus(prev => ({
      ...prev,
      [submissionId]: !prev[submissionId]
    }));
  };

  // Handle action clicks
  const handleTableActionClick = (submissionId, actionType) => {
    setOpenActionMenus(prev => ({ ...prev, [submissionId]: false }));
    const submission = submissions.find(s => s.id === submissionId);
    
    switch(actionType) {
      case 'view':
        setViewingSubmission(submission);
        setShowViewModal(true);
        break;
      case 'edit':
        setEditingSubmission(submission);
        // Sales managers (Sam, Sarah) use client edit modal for status transitions
        // Recruiters (Riley, Lena) use add submission modal for withdrawal only
        if (currentUserRole === 'sales' || currentUserRole === 'sales_manager' || currentUserRole === 'lead_sales_manager') {
          setShowClientEditModal(true);
        } else {
          setShowAddModal(true);
        }
        break;
      case 'delete':
        if (window.confirm('Are you sure you want to delete this submission?')) {
          handleDeleteSubmission(submissionId);
        }
        break;
      case 'update_status':
        showToast('Status update functionality coming soon', 'info');
        break;
      default:
        showToast('Action not implemented', 'warning');
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/${submissionId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSubmissions(prev => (prev || []).filter(s => s.id !== submissionId));
        showToast('Submission deleted successfully', 'success');
      } else {
        showToast('Failed to delete submission', 'error');
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      showToast('Error deleting submission', 'error');
    }
  };

  const handleSubmissionCreated = (newSubmission) => {
    if (editingSubmission) {
      // Update existing submission
      setSubmissions(prev => prev.map(s => s.id === newSubmission.id ? newSubmission : s));
      showToast('Submission updated successfully!', 'success');
    } else {
      // Add new submission
      setSubmissions(prev => [newSubmission, ...(prev || [])]);
      showToast('Submission created successfully!', 'success');
    }
    setShowAddModal(false);
    setEditingSubmission(null);
  };

  // Filter and sort submissions
  const filteredSubmissions = (submissions || []).filter(submission => {
    const matchesSearch = submission.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         submission.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         submission.recruiter_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || submission.current_status === statusFilter;
    const matchesJob = jobFilter === 'all' || submission.job_id === jobFilter;
    
    return matchesSearch && matchesStatus && matchesJob;
  });

  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === 'submission_date' || sortField === 'updated_at') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubmissions = sortedSubmissions.slice(startIndex, startIndex + itemsPerPage);

  // Get unique values for filters
  const uniqueStatuses = [...new Set((submissions || []).map(s => s.current_status).filter(Boolean))];
  const uniqueJobs = [...new Set((submissions || []).map(s => s.job_title).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="submissions-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Submissions</h1>
          <p className="text-gray-600 mt-1">Manage candidate submissions and track progress</p>
        </div>
        <button
          onClick={() => {
            setEditingSubmission(null);
            setShowAddModal(true);
            showToast('Opening submission creation form');
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 shadow-lg"
          data-testid="create-submission-button"
        >
          <Plus className="h-5 w-5" />
          <span>Add Submission</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search submissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>
                {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>

          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Jobs</option>
            {uniqueJobs.slice(0, 10).map(job => (
              <option key={job} value={job}>{job}</option>
            ))}
          </select>

          <div className="flex items-center space-x-2">
            <Send className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">{sortedSubmissions.length} submissions</span>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : sortedSubmissions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Send className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No submissions found</h3>
          <p className="text-gray-600 mb-6">
            {submissions.length === 0 
              ? "Create your first candidate submission to start tracking progress"
              : "No submissions match your current filters"
            }
          </p>
          <button
            onClick={() => {
              if (submissions.length === 0) {
                setEditingSubmission(null);
                setShowAddModal(true);
                showToast('Opening submission creation form');
              } else {
                setSearchTerm('');
                setStatusFilter('all');
                setJobFilter('all');
                showToast('Filters cleared');
              }
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{submissions.length === 0 ? 'Add First Submission' : 'Clear Filters'}</span>
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recruiter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                          <User className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {submission.candidate_name || 'Unknown Candidate'}
                          </div>
                          <div className="text-xs text-gray-500">{submission.candidate_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{submission.job_title || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(submission.current_status)}`}>
                        {submission.current_status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Sourced'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(submission.submission_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission.recruiter_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleTableActionClick(submission.id, 'view')}
                          className="text-purple-600 hover:text-purple-900 flex items-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleTableActionClick(submission.id, 'edit')}
                          className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                ‹
              </button>
              <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Submission Modal */}
      {showAddModal && (
        <AddSubmissionModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setEditingSubmission(null);
          }}
          onSubmissionCreated={handleSubmissionCreated}
          editingSubmission={editingSubmission}
        />
      )}

      {/* View Submission Modal */}
      {showViewModal && (
        <SubmissionViewModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setViewingSubmission(null);
          }}
          submission={viewingSubmission}
        />
      )}

      {/* Client Submission Edit Modal (for Sales Managers) */}
      {showClientEditModal && (
        <ClientSubmissionEditModal
          isOpen={showClientEditModal}
          onClose={() => {
            setShowClientEditModal(false);
            setEditingSubmission(null);
          }}
          submission={editingSubmission}
          onUpdate={handleSubmissionCreated}
        />
      )}
    </div>
  );
};

export default Submissions;